import React, { useState, useEffect } from 'react';
import { transactionsService, categoriesService } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { 
  Receipt, 
  Plus, 
  Pencil, 
  Trash2, 
  RefreshCw,
  Search,
  TrendingUp,
  TrendingDown,
  CalendarIcon,
  Filter,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '../lib/utils';

const TransactionsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    valor: '',
    tipo: 'despesa',
    categoriaId: '',
    data: new Date(),
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [transData, catData] = await Promise.all([
        transactionsService.getAll(),
        categoriesService.getAll()
      ]);
      setTransactions(transData || []);
      setCategories(catData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({
      valor: '',
      tipo: 'despesa',
      categoriaId: '',
      data: new Date(),
    });
  };

  const handleCreate = async () => {
    if (!formData.valor || !formData.categoriaId) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    const valor = parseFloat(formData.valor);
    if (isNaN(valor) || valor <= 0) {
      toast.error('Digite um valor válido');
      return;
    }

    setIsSubmitting(true);
    try {
      await transactionsService.create(
        valor,
        formData.tipo,
        parseInt(formData.categoriaId),
        formData.data.toISOString()
      );
      toast.success('Lançamento criado com sucesso!');
      resetForm();
      setCreateModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error creating transaction:', error);
      toast.error('Erro ao criar lançamento');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!formData.valor || !formData.categoriaId) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    const valor = parseFloat(formData.valor);
    if (isNaN(valor) || valor <= 0) {
      toast.error('Digite um valor válido');
      return;
    }

    setIsSubmitting(true);
    try {
      await transactionsService.update(
        selectedTransaction.id,
        valor,
        formData.tipo,
        parseInt(formData.categoriaId),
        formData.data.toISOString()
      );
      toast.success('Lançamento atualizado com sucesso!');
      setEditModalOpen(false);
      setSelectedTransaction(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error updating transaction:', error);
      toast.error('Erro ao atualizar lançamento');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTransaction) return;

    setIsSubmitting(true);
    try {
      await transactionsService.delete(selectedTransaction.id);
      toast.success('Lançamento excluído com sucesso!');
      setDeleteModalOpen(false);
      setSelectedTransaction(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      toast.error('Erro ao excluir lançamento');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (transaction) => {
    setSelectedTransaction(transaction);
    setFormData({
      valor: transaction.valor.toString(),
      tipo: transaction.tipo?.toLowerCase() || 'despesa',
      categoriaId: transaction.categoriaId.toString(),
      data: transaction.data ? parseISO(transaction.data) : new Date(),
    });
    setEditModalOpen(true);
  };

  const openDeleteModal = (transaction) => {
    setSelectedTransaction(transaction);
    setDeleteModalOpen(true);
  };

  const openCreateModal = () => {
    resetForm();
    setCreateModalOpen(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getCategoryName = (categoriaId) => {
    const category = categories.find(c => c.id === categoriaId);
    return category?.nome || 'Sem categoria';
  };

  // Filter transactions
  const filteredTransactions = transactions.filter(t => {
    const categoryName = getCategoryName(t.categoriaId).toLowerCase();
    const matchesSearch = categoryName.includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || t.tipo?.toLowerCase() === filterType;
    return matchesSearch && matchesType;
  });

  // Sort by date (newest first)
  const sortedTransactions = [...filteredTransactions].sort(
    (a, b) => new Date(b.data) - new Date(a.data)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="transactions-loading">
        <RefreshCw className="w-8 h-8 text-green-400 animate-spin" />
      </div>
    );
  }

  const TransactionForm = ({ onSubmit, submitLabel }) => (
    <div className="space-y-4 py-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-zinc-300">Valor (R$)</Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            data-testid="transaction-value-input"
            placeholder="0,00"
            value={formData.valor}
            onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
            className="bg-zinc-950/50 border-zinc-800 focus:border-green-500 focus:ring-1 focus:ring-green-500 text-white placeholder:text-zinc-600"
          />
        </div>
        
        <div className="space-y-2">
          <Label className="text-zinc-300">Tipo</Label>
          <Select
            value={formData.tipo}
            onValueChange={(value) => setFormData({ ...formData, tipo: value })}
          >
            <SelectTrigger 
              className="bg-zinc-950/50 border-zinc-800 focus:border-green-500 text-white"
              data-testid="transaction-type-select"
            >
              <SelectValue placeholder="Selecione o tipo" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="receita" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  Receita
                </div>
              </SelectItem>
              <SelectItem value="despesa" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">
                <div className="flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-red-400" />
                  Despesa
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-zinc-300">Categoria</Label>
        <Select
          value={formData.categoriaId}
          onValueChange={(value) => setFormData({ ...formData, categoriaId: value })}
        >
          <SelectTrigger 
            className="bg-zinc-950/50 border-zinc-800 focus:border-green-500 text-white"
            data-testid="transaction-category-select"
          >
            <SelectValue placeholder="Selecione uma categoria" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 max-h-[200px]">
            {categories.length > 0 ? (
              categories.map((cat) => (
                <SelectItem 
                  key={cat.id} 
                  value={cat.id.toString()}
                  className="text-white hover:bg-zinc-800 focus:bg-zinc-800"
                >
                  {cat.nome}
                </SelectItem>
              ))
            ) : (
              <div className="p-2 text-zinc-500 text-sm text-center">
                Nenhuma categoria disponível
              </div>
            )}
          </SelectContent>
        </Select>
        {categories.length === 0 && (
          <p className="text-xs text-amber-400">
            Crie uma categoria primeiro para poder adicionar lançamentos
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label className="text-zinc-300">Data</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              data-testid="transaction-date-btn"
              className={cn(
                "w-full justify-start text-left font-normal bg-zinc-950/50 border-zinc-800 hover:bg-zinc-900 hover:border-zinc-700",
                !formData.data && "text-zinc-600"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {formData.data ? (
                format(formData.data, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
              ) : (
                <span>Selecione uma data</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800" align="start">
            <Calendar
              mode="single"
              selected={formData.data}
              onSelect={(date) => setFormData({ ...formData, data: date || new Date() })}
              initialFocus
              locale={ptBR}
              className="bg-zinc-900"
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="transactions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white font-['Manrope']">
            Lançamentos
          </h1>
          <p className="text-zinc-400 mt-1">
            Gerencie suas receitas e despesas
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={fetchData}
            variant="outline"
            className="border-zinc-700 text-zinc-300 hover:border-green-500 hover:text-green-400"
            data-testid="refresh-transactions-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button
            onClick={openCreateModal}
            className="bg-green-600 hover:bg-green-500 text-white shadow-[0_0_15px_rgba(34,197,94,0.4)]"
            data-testid="new-transaction-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Lançamento
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            type="text"
            placeholder="Buscar por categoria..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-zinc-950/50 border-zinc-800 focus:border-green-500 focus:ring-1 focus:ring-green-500 text-white placeholder:text-zinc-600"
            data-testid="search-transactions-input"
          />
        </div>
        
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger 
            className="w-full sm:w-[180px] bg-zinc-950/50 border-zinc-800 focus:border-green-500 text-white"
            data-testid="filter-type-select"
          >
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filtrar por tipo" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">
              Todos
            </SelectItem>
            <SelectItem value="receita" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-400" />
                Receitas
              </div>
            </SelectItem>
            <SelectItem value="despesa" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">
              <div className="flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-red-400" />
                Despesas
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Transactions List */}
      {sortedTransactions.length > 0 ? (
        <div className="space-y-4">
          {sortedTransactions.map((transaction) => {
            const isReceita = transaction.tipo?.toLowerCase() === 'receita';
            
            return (
              <Card 
                key={transaction.id}
                className="bg-zinc-900/50 border border-white/10 rounded-xl group hover:border-green-500/30 transition-colors"
                data-testid={`transaction-card-${transaction.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-xl ${isReceita ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                        {isReceita ? (
                          <TrendingUp className="w-6 h-6 text-green-400" />
                        ) : (
                          <TrendingDown className="w-6 h-6 text-red-400" />
                        )}
                      </div>
                      <div>
                        <h3 className="text-white font-medium text-lg">
                          {getCategoryName(transaction.categoriaId)}
                        </h3>
                        <div className="flex items-center gap-3 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            isReceita 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {isReceita ? 'Receita' : 'Despesa'}
                          </span>
                          <span className="text-sm text-zinc-500">
                            {transaction.data 
                              ? format(parseISO(transaction.data), "dd/MM/yyyy", { locale: ptBR })
                              : '-'
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between sm:justify-end gap-4">
                      <p className={`text-xl font-bold font-mono ${isReceita ? 'text-green-400' : 'text-red-400'}`}>
                        {isReceita ? '+' : '-'}{formatCurrency(transaction.valor)}
                      </p>
                      
                      <div className="flex gap-2 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(transaction)}
                          className="hover:bg-green-500/10 hover:text-green-400 text-zinc-400"
                          data-testid={`edit-transaction-${transaction.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteModal(transaction)}
                          className="hover:bg-red-500/10 hover:text-red-400 text-zinc-400"
                          data-testid={`delete-transaction-${transaction.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="bg-zinc-900/50 border border-white/10 rounded-xl">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="p-4 rounded-full bg-zinc-800/50 mb-4">
              <FileText className="w-8 h-8 text-zinc-500" />
            </div>
            <h3 className="text-white font-medium text-lg mb-2">
              {searchTerm || filterType !== 'all' 
                ? 'Nenhum lançamento encontrado' 
                : 'Nenhum lançamento registrado'
              }
            </h3>
            <p className="text-zinc-500 text-sm mb-6 text-center max-w-md">
              {searchTerm || filterType !== 'all'
                ? 'Tente ajustar os filtros de busca' 
                : 'Registre seu primeiro lançamento para começar a controlar suas finanças'
              }
            </p>
            {!searchTerm && filterType === 'all' && (
              <Button
                onClick={openCreateModal}
                className="bg-green-600 hover:bg-green-500 text-white"
                data-testid="empty-state-new-transaction-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Lançamento
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Novo Lançamento</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Registre uma nova receita ou despesa
            </DialogDescription>
          </DialogHeader>
          <TransactionForm />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateModalOpen(false)}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreate}
              disabled={isSubmitting || categories.length === 0}
              className="bg-green-600 hover:bg-green-500 text-white"
              data-testid="submit-new-transaction-btn"
            >
              {isSubmitting ? 'Criando...' : 'Criar Lançamento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Editar Lançamento</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Altere os dados do lançamento
            </DialogDescription>
          </DialogHeader>
          <TransactionForm />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditModalOpen(false)}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleEdit}
              disabled={isSubmitting}
              className="bg-green-600 hover:bg-green-500 text-white"
              data-testid="submit-edit-transaction-btn"
            >
              {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white font-['Manrope']">
              Excluir lançamento?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Tem certeza que deseja excluir este lançamento de{' '}
              <span className={selectedTransaction?.tipo?.toLowerCase() === 'receita' ? 'text-green-400' : 'text-red-400'}>
                {selectedTransaction && formatCurrency(selectedTransaction.valor)}
              </span>
              ? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isSubmitting}
              className="bg-red-600 hover:bg-red-500 text-white"
              data-testid="confirm-delete-transaction-btn"
            >
              {isSubmitting ? 'Excluindo...' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TransactionsPage;
